import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import StripePayment from '../StripePayment';
import { config } from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';

type BundleOption = {
  template_id: string;
  name: string;
  session_count: number;
  price: number;
  per_session_price: number;
};

type OptionsResponse = {
  student: { id: string; name: string; tier: string };
  current_billing_plan?: { id: string; plan_type: string; status: string } | null;
  allowed_program_types: string[];
  applicability_options: string[];
  bundle_options: BundleOption[];
};

type PurchaseStartResponse = {
  success: boolean;
  invoice_id: string;
  invoice_number: string;
  amount: number;
  student_name: string;
  student_id: string;
  session_count: number;
  tier: string;
  applicability: string[];
  stripe_metadata: Record<string, any>;
};

interface AddSessionModalProps {
  studentId: string;
  studentName: string;
  onClose: () => void;
  onPurchased: () => void;
}

const formatProgramApplicabilityLabel = (applicability: string[] | undefined) => {
  const normalized = (applicability || []).map((item) => item.toLowerCase());
  const hasAfterSchool = normalized.includes('after_school');
  const hasHomeSchool = normalized.includes('home_school');

  if (hasAfterSchool && hasHomeSchool) return 'After School + Home School';
  if (hasHomeSchool) return 'Home School';
  return 'After School';
};

const AddSessionModal: React.FC<AddSessionModalProps> = ({
  studentId,
  studentName,
  onClose,
  onPurchased,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<OptionsResponse | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedApplicability, setSelectedApplicability] = useState<string>('');
  const [starting, setStarting] = useState(false);
  const [purchaseCtx, setPurchaseCtx] = useState<PurchaseStartResponse | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingInteracMessage, setPendingInteracMessage] = useState<string | null>(null);
  const [interacSubmitted, setInteracSubmitted] = useState(false);

  const getAuthToken = async () => {
    const localToken =
      localStorage.getItem('cognitoToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      '';
    if (localToken) {
      return localToken;
    }

    try {
      const session = await fetchAuthSession();
      const sessionToken =
        session.tokens?.idToken?.toString() ||
        session.tokens?.accessToken?.toString() ||
        '';
      if (sessionToken) {
        localStorage.setItem('cognitoToken', sessionToken);
      }
      return sessionToken;
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAuthToken();
        if (!token) {
          throw new Error('Authentication token missing. Please sign out and sign in again.');
        }
        const response = await axios.get(
          `${config.BACKEND_URL}/api/packages/children/${studentId}/add-session-options`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = response.data as OptionsResponse;
        setOptions(data);
        if (data.bundle_options.length > 0) {
          setSelectedTemplateId(data.bundle_options[0].template_id);
        }
        if (data.applicability_options.length > 0) {
          setSelectedApplicability(data.applicability_options[0]);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load add-session options');
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [studentId]);

  const selectedBundle = useMemo(() => {
    if (!options) return null;
    return options.bundle_options.find((o) => o.template_id === selectedTemplateId) || null;
  }, [options, selectedTemplateId]);

  const startPurchase = async () => {
    if (!selectedBundle || !selectedApplicability) return;
    try {
      setStarting(true);
      setError(null);
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token missing. Please sign out and sign in again.');
      }
      const response = await axios.post(
        `${config.BACKEND_URL}/api/packages/purchases/start`,
        {
          student_id: studentId,
          template_id: selectedBundle.template_id,
          session_count: selectedBundle.session_count,
          applicability: selectedApplicability,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = response.data as PurchaseStartResponse;
      setPurchaseCtx(data);
      setShowPayment(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start purchase');
    } finally {
      setStarting(false);
    }
  };

  const confirmStripePayment = async (paymentIntent: any) => {
    if (!purchaseCtx) return;
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token missing. Please sign out and sign in again.');
      }
      const response = await axios.post(
        `${config.BACKEND_URL}/api/packages/purchases/confirm`,
        {
          invoice_id: purchaseCtx.invoice_id,
          stripe_payment_intent_id: paymentIntent?.id || paymentIntent?.payment_intent_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = response.data || {};
      setSuccessMessage(
        `Payment successful. ${purchaseCtx.session_count} sessions added for ${purchaseCtx.student_name}. Invoice ${purchaseCtx.invoice_number}.`
      );
      setShowPayment(false);
      onPurchased();
      if (data.package_id) {
        // No-op marker to keep response consumed and avoid lint about unused data object.
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Payment succeeded but confirmation failed');
    }
  };

  const sendInteracInstructions = async () => {
    if (!purchaseCtx) return;
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token missing. Please sign out and sign in again.');
      }
      await axios.post(
        `${config.BACKEND_URL}/api/packages/purchases/interac`,
        { invoice_id: purchaseCtx.invoice_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setPendingInteracMessage(
        `Interac instructions sent for invoice ${purchaseCtx.invoice_number}. Sessions will be activated after payment reconciliation.`
      );
      setInteracSubmitted(true);
      setShowPayment(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send Interac instructions');
    }
  };

  return (
    <>
      {!showPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 10000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '620px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 12px 42px rgba(0,0,0,0.25)',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #eaeaea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <h3 style={{ margin: 0 }}>Add Sessions for {studentName}</h3>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>

            <div style={{ padding: '20px' }}>
              {loading && <p>Loading options...</p>}
              {error && (
                <div style={{ padding: '10px', background: '#ffe8e8', color: '#8c1d1d', borderRadius: '8px', marginBottom: '12px' }}>
                  {error}
                </div>
              )}
              {successMessage && (
                <div style={{ padding: '10px', background: '#e8f7ec', color: '#165f2d', borderRadius: '8px', marginBottom: '12px' }}>
                  {successMessage}
                </div>
              )}
              {pendingInteracMessage && (
                <div style={{ padding: '10px', background: '#fff7e6', color: '#8a5a00', borderRadius: '8px', marginBottom: '12px' }}>
                  {pendingInteracMessage}
                </div>
              )}

              {interacSubmitted && purchaseCtx ? (
                <div style={{ border: '1px solid #e6e6e6', borderRadius: '10px', padding: '16px', background: '#fafafa' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '8px' }}>Interac Instructions Sent</h4>
                  <p style={{ margin: '0 0 10px 0', color: '#444' }}>
                    We emailed Interac instructions for invoice <strong>{purchaseCtx.invoice_number}</strong>.
                  </p>
                  <p style={{ margin: '0 0 10px 0', color: '#444' }}>
                    Sessions will be activated after payment reconciliation.
                  </p>
                  <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
                    <div><strong>Invoice:</strong> {purchaseCtx.invoice_number}</div>
                    <div><strong>Student:</strong> {purchaseCtx.student_name}</div>
                    <div><strong>Bundle:</strong> {purchaseCtx.session_count} sessions</div>
                    <div><strong>Amount:</strong> ${purchaseCtx.amount.toFixed(2)} CAD</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setInteracSubmitted(false);
                        setPendingInteracMessage(null);
                        setPurchaseCtx(null);
                      }}
                    >
                      Add Another Package
                    </button>
                    <button className="btn btn-primary" onClick={onClose}>
                      Done
                    </button>
                  </div>
                </div>
              ) : !loading && options && (
                <>
                  <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                    <p style={{ margin: 0 }}><strong>Student Tier:</strong> {options.student.tier}</p>
                    {options.current_billing_plan && (
                      <p style={{ margin: '6px 0 0 0' }}>
                        <strong>Current Billing Plan:</strong> {options.current_billing_plan.plan_type} ({options.current_billing_plan.status})
                      </p>
                    )}
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>Choose session bundle</label>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {options.bundle_options.map((bundle) => (
                        <label key={bundle.template_id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="bundle"
                            value={bundle.template_id}
                            checked={selectedTemplateId === bundle.template_id}
                            onChange={() => setSelectedTemplateId(bundle.template_id)}
                            style={{ marginRight: '8px' }}
                          />
                          {bundle.session_count} sessions - ${bundle.price.toFixed(2)} (${bundle.per_session_price.toFixed(2)}/session)
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '18px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>Applicable programs</label>
                    <select
                      value={selectedApplicability}
                      onChange={(e) => setSelectedApplicability(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d0d7de' }}
                    >
                      {options.applicability_options.map((option) => (
                        <option key={option} value={option}>
                          {option === 'both' ? 'Both (After School + Home School)' : option.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>Total:</strong> ${selectedBundle ? selectedBundle.price.toFixed(2) : '0.00'}
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={startPurchase}
                      disabled={!selectedBundle || !selectedApplicability || starting}
                    >
                      {starting ? 'Preparing...' : 'Continue to Payment'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {purchaseCtx && (
        <StripePayment
          show={showPayment}
          amount={purchaseCtx.amount}
          description={`Add ${purchaseCtx.session_count} sessions for ${purchaseCtx.student_name}`}
          metadata={purchaseCtx.stripe_metadata}
          interacContext={{
            eventName: `Session Add-on (${purchaseCtx.session_count})`,
            studentName: purchaseCtx.student_name,
            messageOverride: `${formatProgramApplicabilityLabel(purchaseCtx.applicability)}, ${purchaseCtx.student_name}`,
          }}
          onSuccess={confirmStripePayment}
          onError={(message) => setError(message)}
          onCancel={() => setShowPayment(false)}
          onInteracSelected={sendInteracInstructions}
        />
      )}
    </>
  );
};

export default AddSessionModal;

